// Normalise a Faq or ResourceDocument row into a unified LearningItem shape
// the Hub UI can render. Keeps the two backend tables decoupled while
// letting the page treat them identically.
//
// Shape:
//   { id, source: 'faq'|'doc', kind: 'video'|'image'|'pdf'|'link'|'text',
//     category, subCategory, title, description,
//     mediaUrls: string[],   // images
//     videoUrls: string[],   // youtube
//     fileUrl: string|null,  // server file
//     externalUrl: string|null,
//     fileName, fileSize,    // doc only
//     sortOrder: number }

function parseList(json) {
    if (!json) return [];
    try {
        const arr = JSON.parse(json);
        return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch {
        return String(json).split('\n').map((s) => s.trim()).filter(Boolean);
    }
}

export function normaliseTutorial(t) {
    const videoUrls = parseList(t.videoLinks);
    const mediaUrls = parseList(t.imageUrls);
    const kind = videoUrls.length ? 'video' : mediaUrls.length ? 'image' : 'text';
    return {
        id: t.tutorialId,
        source: 'tutorial',
        kind,
        category: t.category || '',
        subCategory: t.subCategory || '',
        title: t.title || '',
        titleEn: t.titleEn || '',
        titleMy: t.titleMy || '',
        description: t.description || '',
        descriptionEn: t.descriptionEn || '',
        descriptionMy: t.descriptionMy || '',
        mediaUrls,
        videoUrls,
        fileUrl: null,
        externalUrl: null,
        fileName: null,
        fileSize: null,
        sortOrder: t.sortOrder ?? 0,
        coverUrl: t.coverUrl || null,
        attachments: t.attachments || null,
    };
}

export function normaliseFaq(faq) {
    const videoUrls = parseList(faq.videoLinks);
    const mediaUrls = parseList(faq.imageUrls);
    const kind = videoUrls.length ? 'video' : mediaUrls.length ? 'image' : 'text';
    return {
        id: faq.faqId,
        source: 'faq',
        kind,
        category: faq.category || '',
        subCategory: faq.subCategory || '',
        title: faq.title || '',
        description: faq.description || '',
        mediaUrls,
        videoUrls,
        fileUrl: null,
        externalUrl: null,
        fileName: null,
        fileSize: null,
        sortOrder: faq.sortOrder ?? 0,
    };
}

export function normaliseDocument(doc) {
    const kind = doc.type === 'link' ? 'link' : 'pdf';
    return {
        id: doc.documentId,
        source: 'doc',
        kind,
        category: doc.category || '',
        subCategory: doc.subCategory || '',
        title: doc.title || '',
        description: doc.description || '',
        mediaUrls: [],
        videoUrls: [],
        fileUrl: doc.fileUrl || null,
        externalUrl: doc.externalUrl || null,
        fileName: doc.fileName || null,
        fileSize: doc.fileSize || null,
        sortOrder: doc.sortOrder ?? 0,
    };
}

// The 5-bucket display order. Any unknown category sorts to the end.
export const CATEGORY_ORDER = [
    'About Astern',
    'Product Specsheets',
    'Sales Templates',
    'Payment & Financing',
    'Onboarding & Training',
];

// Sub-category display order (only Product Specsheets uses these today).
export const SUBCATEGORY_ORDER = [
    'Panels',
    'Inverters',
    'Batteries',
    'Accessories',
];

export function compareCategory(a, b) {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
}

export function compareSubCategory(a, b) {
    const ai = SUBCATEGORY_ORDER.indexOf(a);
    const bi = SUBCATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return (a || '').localeCompare(b || '');
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
}

// Group an array of normalised items into a 2-level structure:
//   [{ category, items, subGroups: [{ subCategory, items }] }]
// Items without a subCategory live directly under the category; items with
// one are bundled into subGroups, ordered by SUBCATEGORY_ORDER.
export function groupItems(items) {
    const byCategory = new Map();
    for (const item of items) {
        const cat = item.category || 'Uncategorised';
        if (!byCategory.has(cat)) byCategory.set(cat, []);
        byCategory.get(cat).push(item);
    }
    // Lowest SortOrder within a set of items — drives category/sub-category order.
    const minOrder = (arr) => Math.min(...arr.map((i) => i.sortOrder ?? 0));
    const groups = [];
    for (const [category, catItems] of byCategory) {
        const direct = [];
        const subBy = new Map();
        for (const item of catItems) {
            if (item.subCategory) {
                if (!subBy.has(item.subCategory)) subBy.set(item.subCategory, []);
                subBy.get(item.subCategory).push(item);
            } else {
                direct.push(item);
            }
        }
        const subGroups = [...subBy.entries()]
            .map(([subCategory, subItems]) => ({
                subCategory,
                items: subItems.sort((x, y) => (x.sortOrder - y.sortOrder) || x.title.localeCompare(y.title)),
            }))
            // Order sub-categories by their lowest SortOrder; tie-break with the
            // fixed SUBCATEGORY_ORDER list (preserves FAQ/Documents where SortOrder=0).
            .sort((a, b) => (minOrder(a.items) - minOrder(b.items)) || compareSubCategory(a.subCategory, b.subCategory));
        groups.push({
            category,
            items: direct.sort((x, y) => (x.sortOrder - y.sortOrder) || x.title.localeCompare(y.title)),
            subGroups,
        });
    }
    // Order categories by their lowest SortOrder; tie-break with CATEGORY_ORDER.
    return groups.sort((a, b) =>
        (minOrder(byCategory.get(a.category)) - minOrder(byCategory.get(b.category))) ||
        compareCategory(a.category, b.category));
}
